
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface SiteAvailabilityContextType {
  refreshAvailability: (siteId?: string) => Promise<void>;
  checkAvailability: (siteId: string, startDate: Date, endDate: Date) => Promise<boolean>;
  availabilityData: Record<string, any>;
  refreshedSiteIds: string[];
  notifyBookingCompleted: (siteId: string, startDate: string, endDate: string) => void;
  isLoading: boolean;
  lastRefresh: Date | null;
  forceRefreshSite: (siteId: string) => Promise<boolean>;
}

const SiteAvailabilityContext = createContext<SiteAvailabilityContextType | undefined>(undefined);

export const SiteAvailabilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [availabilityData, setAvailabilityData] = useState<Record<string, any>>({});
  const [refreshedSiteIds, setRefreshedSiteIds] = useState<string[]>([]);
  const subscriptionsRef = useRef<any[]>([]);
  const isSubscribedRef = useRef(false);

  const refreshAvailability = useCallback(async (siteId?: string) => {
    setIsLoading(true);
    try {
      if (siteId) {
        // Refresh specific site
        const { data, error } = await supabase
          .from('camping_sites')
          .select('*')
          .eq('id', siteId)
          .single();
        
        if (error) {
          console.error('Error refreshing site availability:', error);
        } else {
          setAvailabilityData(prev => ({
            ...prev,
            [siteId]: { ...data, available: true }
          }));
          setRefreshedSiteIds(prev => [...prev.filter(id => id !== siteId), siteId]);
          console.log(`Site ${siteId} availability refreshed`);
        }
      } else {
        // Refresh all sites
        const { error } = await supabase
          .from('camping_sites')
          .select('id')
          .limit(1);
        
        if (error) {
          console.error('Error refreshing availability:', error);
          toast({
            title: "Refresh Error",
            description: "Failed to refresh site availability data.",
            variant: "destructive"
          });
        } else {
          setLastRefresh(new Date());
          console.log('Site availability refreshed successfully');
        }
      }
    } catch (error) {
      console.error('Unexpected error during refresh:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const checkAvailability = useCallback(async (siteId: string, startDate: Date, endDate: Date): Promise<boolean> => {
    try {
      const formattedStartDate = startDate.toISOString().split('T')[0];
      const formattedEndDate = endDate.toISOString().split('T')[0];
      
      const { data: isAvailable, error } = await supabase
        .rpc('check_site_availability_for_dates', {
          site_id: siteId,
          check_start_date: formattedStartDate,
          check_end_date: formattedEndDate
        });
      
      if (error) {
        console.error('Error checking site availability:', error);
        return false;
      }
      
      const available = !!isAvailable;
      
      // Update local availability data
      setAvailabilityData(prev => ({
        ...prev,
        [siteId]: { 
          ...prev[siteId], 
          available,
          lastChecked: new Date().toISOString()
        }
      }));
      
      return available;
    } catch (err) {
      console.error('Error in checkAvailability:', err);
      return false;
    }
  }, []);

  // NEW: Force refresh a specific site and return its availability
  const forceRefreshSite = useCallback(async (siteId: string): Promise<boolean> => {
    try {
      // First check if there are any paid bookings for this site that might conflict
      const { data: bookings, error: bookingsError } = await supabase
        .from('site_bookings')
        .select('start_date, end_date, payment_status')
        .eq('site_id', siteId)
        .eq('payment_status', 'paid');

      if (bookingsError) {
        console.error('Error checking bookings during force refresh:', bookingsError);
        return true; // Default to available if we can't check
      }

      // Update our local state with the current booking information
      const hasActiveBookings = bookings && bookings.length > 0;
      
      setAvailabilityData(prev => ({
        ...prev,
        [siteId]: {
          ...prev[siteId],
          available: !hasActiveBookings,
          lastForceRefresh: new Date().toISOString(),
          activeBookings: bookings
        }
      }));

      // Also trigger a general refresh
      await refreshAvailability(siteId);
      
      return !hasActiveBookings;
    } catch (error) {
      console.error('Error in forceRefreshSite:', error);
      return true; // Default to available on error
    }
  }, [refreshAvailability]);

  const notifyBookingCompleted = useCallback((siteId: string, startDate: string, endDate: string) => {
    console.log(`Booking completed notification for site ${siteId}: ${startDate} to ${endDate}`);
    
    // Mark site as potentially unavailable
    setAvailabilityData(prev => ({
      ...prev,
      [siteId]: { 
        ...prev[siteId], 
        available: false,
        lastBooking: { startDate, endDate, completedAt: new Date().toISOString() }
      }
    }));
    
    // Trigger a refresh of this site
    refreshAvailability(siteId);
  }, [refreshAvailability]);

  const setupRealTimeSubscriptions = useCallback(() => {
    // Prevent duplicate subscriptions
    if (isSubscribedRef.current) {
      console.log('Real-time subscriptions already active');
      return;
    }

    console.log('Setting up real-time subscriptions...');

    try {
      // Subscribe to site bookings changes
      const siteBookingsChannel = supabase
        .channel('site_bookings_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'site_bookings'
          },
          (payload) => {
            console.log('Site bookings change detected:', payload);
            setLastRefresh(new Date());
            
            // Update availability for affected site
            const newData = payload.new as Record<string, any> | null;
            if (newData && newData.site_id) {
              // Force refresh the specific site that was affected
              forceRefreshSite(newData.site_id);
              
              if (newData.payment_status === 'paid') {
                setAvailabilityData(prev => ({
                  ...prev,
                  [newData.site_id]: { 
                    ...prev[newData.site_id], 
                    available: false,
                    lastBooking: {
                      startDate: newData.start_date,
                      endDate: newData.end_date,
                      bookingId: newData.booking_id
                    }
                  }
                }));
              }
            }
          }
        )
        .subscribe((status) => {
          console.log('Real-time subscription status for site_bookings:', status);
          if (status === 'SUBSCRIBED') {
            console.log('Successfully subscribed to site_bookings changes');
          } else if (status === 'CHANNEL_ERROR') {
            console.error('Error subscribing to site_bookings changes');
            setTimeout(() => {
              if (!isSubscribedRef.current) {
                setupRealTimeSubscriptions();
              }
            }, 30000);
          }
        });

      // Subscribe to camping sites changes
      const campingSitesChannel = supabase
        .channel('camping_sites_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'camping_sites'
          },
          (payload) => {
            console.log('Camping sites change detected:', payload);
            setLastRefresh(new Date());
            
            // Update specific site data if available
            const newData = payload.new as Record<string, any> | null;
            const oldData = payload.old as Record<string, any> | null;
            if (newData && newData.id) {
              const statusChanged = oldData ? newData.status !== oldData.status : false;
              setAvailabilityData(prev => ({
                ...prev,
                [newData.id]: { 
                  ...prev[newData.id], 
                  ...newData,
                  // Only assume "available: true" when the site is active.
                  // If status changed, signal that consumers should refetch.
                  available: newData.status === 'active',
                  statusChanged
                }
              }));
            }
          }
        )
        .subscribe((status) => {
          console.log('Real-time subscription status for camping_sites:', status);
          if (status === 'SUBSCRIBED') {
            console.log('Successfully subscribed to camping_sites changes');
          } else if (status === 'CHANNEL_ERROR') {
            console.error('Error subscribing to camping_sites changes');
            setTimeout(() => {
              if (!isSubscribedRef.current) {
                setupRealTimeSubscriptions();
              }
            }, 30000);
          }
        });

      // Store channel references for cleanup
      subscriptionsRef.current = [campingSitesChannel, siteBookingsChannel];
      isSubscribedRef.current = true;

    } catch (error) {
      console.error('Error setting up real-time subscriptions:', error);
      isSubscribedRef.current = false;
    }
  }, [forceRefreshSite]);

  useEffect(() => {
    // Initial setup with delay to ensure Supabase is ready
    const timer = setTimeout(() => {
      setupRealTimeSubscriptions();
    }, 1000);

    return () => {
      clearTimeout(timer);
      // Cleanup subscriptions
      subscriptionsRef.current.forEach(channel => {
        if (channel) {
          try {
            supabase.removeChannel(channel);
          } catch (error) {
            console.error('Error removing channel:', error);
          }
        }
      });
      subscriptionsRef.current = [];
      isSubscribedRef.current = false;
    };
  }, [setupRealTimeSubscriptions]);

  const value = {
    refreshAvailability,
    checkAvailability,
    availabilityData,
    refreshedSiteIds,
    notifyBookingCompleted,
    isLoading,
    lastRefresh,
    forceRefreshSite,
  };

  return (
    <SiteAvailabilityContext.Provider value={value}>
      {children}
    </SiteAvailabilityContext.Provider>
  );
};

export const useSiteAvailability = () => {
  const context = useContext(SiteAvailabilityContext);
  if (context === undefined) {
    throw new Error("useSiteAvailability must be used within a SiteAvailabilityProvider");
  }
  return context;
};
